/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { cssProps } from '@oracle/oraclejet-cssprops';
import { classNames } from '../utils/UNSAFE_classNames';
import { useUser } from '../hooks/UNSAFE_useUser';
import { ColorProps } from '../utils/UNSAFE_interpolations/colors';

type ReferenceLineProps = {
  orientation: 'horizontal' | 'vertical';
  percentage: string;
  position?: 'start' | 'end';
  color?: ColorProps['color'];
};

export function ReferenceLineBar({ orientation, percentage, color, position }: ReferenceLineProps) {
  const isHoriz = orientation === 'horizontal';
  const { direction } = useUser();
  const isRtl = direction === 'rtl';
  const isStartPos = position === 'start';
  return (
    <div
      class={classNames([
        styles.base,
        styles[`${orientation}Border`],
        styles[`${orientation}`],
        isStartPos
          ? isHoriz
            ? styles.horiontalStart
            : styles[`verticalStart${isRtl ? 'Rtl' : 'Ltr'}`]
          : ''
      ])}
      style={{
        left: isHoriz && !isRtl ? percentage : undefined,
        right: isHoriz && isRtl ? percentage : undefined,
        bottom: isHoriz ? undefined : percentage,
        backgroundColor: color
      }}></div>
  );
}

const styles = {
  base: cssProps`${{
    backgroundColor: 'var(--oj-c-PRIVATE-DO-NOT-USE-dvt-reference-object-line-color)',
    backgroundClip: 'padding-box',
    position: 'absolute',
    pointerEvents: 'none'
  }}`,
  verticalBorder: cssProps`${{
    borderColor: 'var(--oj-c-PRIVATE-DO-NOT-USE-dvt-contrast-line-color)',
    borderStyle: 'solid',
    borderWidth: '0 0 1px 0'
  }}`,
  horizontalBorder: cssProps`${{
    borderColor: 'var(--oj-c-PRIVATE-DO-NOT-USE-dvt-contrast-line-color)',
    borderStyle: 'solid',
    borderWidth: '0 1px 0 0'
  }}`,
  vertical: cssProps`${{
    width: 'var(--oj-c-PRIVATE-DO-NOT-USE-core-spacing-5x)',
    height: '2px',
    translate: '0 1px'
  }}`,
  horizontal: cssProps`${{
    translate: '-1px 0',
    width: '2px',
    height: 'var(--oj-c-PRIVATE-DO-NOT-USE-core-spacing-5x)'
  }}`,
  horiontalStart: cssProps`${{
    bottom: '0%'
  }}`,
  verticalStartLtr: cssProps`${{
    right: '0%'
  }}`,
  verticalStartRtl: cssProps`${{
    left: '0%'
  }}`
};
